import React, { useEffect, useMemo, useState } from 'react';
import {
  Braces, ChevronLeft, ChevronRight, Files, Megaphone, Phone, Receipt, Rocket, Settings, Shuffle, SlidersHorizontal, Sparkles,
  TrendingUp, ClipboardList, Upload, UserCog, Users, Workflow,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useIsCompact } from '../../hooks/use-mobile.tsx';
import { getSettingsEntryForTab, getSettingsMenu } from '../../config/settingsNav';

// Explicit map (a namespace import would pull every lucide icon into the bundle).
const ICONS = { Braces, Files, Megaphone, Phone, Receipt, Rocket, Shuffle, SlidersHorizontal, Sparkles, TrendingUp, ClipboardList, Upload, UserCog, Users, Workflow };

function EntryIcon({ name, className }) {
  const Icon = (name && ICONS[name]) || Settings;
  return <Icon className={className} aria-hidden />;
}

/**
 * Settings hub: category menu on the left, the active settings page on the
 * right. The menu lists only entries the user can open (Page Visibility still
 * controls each page). On phones / iPad portrait the menu is its own screen
 * and each page gets a "Settings" back link, like the mobile Email pane flow.
 *
 * activeTab:   current page id (any id of a merged entry, or an alias)
 * canAccess:   (pageId) => boolean
 * onNavigate:  (pageId) => void   (guarded by unsaved-change checks upstream)
 * openMenu:    compact only — show the menu screen (set when arriving from the sidebar)
 */
export default function SettingsLayout({ activeTab, canAccess, onNavigate, openMenu = false, children }) {
  const compact = useIsCompact();
  const menu = useMemo(() => getSettingsMenu(canAccess), [canAccess]);
  const activeEntry = getSettingsEntryForTab(activeTab);
  const [showMenu, setShowMenu] = useState(openMenu);

  useEffect(() => { if (openMenu) setShowMenu(true); }, [openMenu]);

  const go = (entry) => {
    if (compact) setShowMenu(false);
    if (entry.key !== activeEntry?.key) onNavigate(entry.targetTab);
  };

  const menuNode = (
    <nav aria-label="Settings" className="space-y-5 p-3">
      {menu.map((category) => (
        <div key={category.id}>
          <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{category.label}</div>
          <ul className="space-y-0.5">
            {category.entries.map((entry) => {
              const active = entry.key === activeEntry?.key;
              return (
                <li key={entry.key}>
                  <button
                    type="button"
                    onClick={() => go(entry)}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-md px-2 text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                      compact ? 'min-h-[44px]' : 'h-8',
                      active ? 'bg-accent font-medium text-foreground' : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                    )}
                  >
                    <EntryIcon name={entry.icon} className="h-4 w-4 shrink-0" />
                    <span className="min-w-0 flex-1 truncate">{entry.label}</span>
                    {compact ? <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );

  if (compact) {
    if (showMenu) {
      return (
        <div className="h-full overflow-y-auto bg-background">
          <div className="px-4 pb-1 pt-4 text-lg font-semibold text-foreground">Settings</div>
          {menuNode}
        </div>
      );
    }
    return (
      <div className="flex h-full min-h-0 flex-col bg-background">
        <div className="flex h-11 shrink-0 items-center border-b border-border px-2">
          <button
            type="button"
            onClick={() => setShowMenu(true)}
            className="inline-flex min-h-[40px] items-center gap-1 rounded-md px-2 text-sm text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            Settings
          </button>
        </div>
        <div className="min-h-0 flex-1">{children}</div>
      </div>
    );
  }

  return (
    <div className="grid h-full min-h-0 grid-cols-[15rem_minmax(0,1fr)] bg-background">
      <aside className="min-h-0 overflow-y-auto border-r border-border bg-card/50">
        <div className="flex items-center gap-2 px-5 pb-1 pt-5 text-sm font-semibold text-foreground">
          <Settings className="h-4 w-4" aria-hidden />
          Settings
        </div>
        {menuNode}
      </aside>
      <div className="min-h-0 min-w-0">{children}</div>
    </div>
  );
}
